import React, { useState, useMemo } from 'react';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useProviders } from '../../hooks/useProviders';
import { useCategories } from '../../hooks/useCategories';
import { PageHeader, StatsCards, FilterBar, ErrorState, type FilterConfig, type StatCard } from '../../components/Common';
import { useReportApi } from './useReportApi';
import { useDebouncedValue } from './useBanksReport';

const fmt = (n: any) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// Paid/remaining are detected from the linked bill (backend includes them
// when the purchase has a bill relation); '-' when no bill is linked
const purchasePaid = (p: any) => p.totalPaid ?? p.bill?.totalPaid;
const purchaseRemaining = (p: any) =>
  p.remainingAmount ?? p.netRemaining ?? p.bill?.remainingAmount ?? p.bill?.netRemaining;
const fmtOrDash = (n: any) => (n === undefined || n === null ? '-' : fmt(n));

const statusMap: Record<string, { label: string; cls: string }> = {
  delivered: { label: 'مُسلم', cls: 'bg-green-100 text-green-800' },
  approved: { label: 'معتمد', cls: 'bg-blue-100 text-blue-800' },
  pending: { label: 'قيد الانتظار', cls: 'bg-yellow-100 text-yellow-800' },
};

const PurchaseItemsDetails: React.FC<{ items: any[]; currencyCode: string }> = ({ items, currencyCode }) => {
  if (!items || items.length === 0) return <p className="text-sm text-gray-500 py-2">لا توجد أصناف لهذا الطلب</p>;
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <p className="text-sm font-semibold text-gray-700 mb-2">الأصناف ({items.length})</p>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-right text-xs text-gray-500">
            <th className="px-3 py-1">الصنف</th>
            <th className="px-3 py-1">الكمية</th>
            <th className="px-3 py-1">سعر الوحدة</th>
            <th className="px-3 py-1">الإجمالي</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item: any, idx: number) => (
            <tr key={idx} className="text-right border-t border-gray-200">
              <td className="px-3 py-1.5 text-gray-900">{item.name || '-'}</td>
              <td className="px-3 py-1.5 text-gray-600">{item.quantity}</td>
              <td className="px-3 py-1.5 text-gray-600">{currencyCode} {fmt(item.unitPrice)}</td>
              <td className="px-3 py-1.5 font-medium text-gray-900">{currencyCode} {fmt(item.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PurchasesReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedPurchase, setExpandedPurchase] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(searchTerm);
  const lookupQO = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: providersData = [] } = useProviders(lookupQO);
  const { data: categoriesData = [] } = useCategories(lookupQO);

  const params = useMemo(() => ({
    status: statusFilter, provider: providerFilter, category: categoryFilter,
    dateFrom, dateTo, search: debouncedSearch,
  }), [statusFilter, providerFilter, categoryFilter, dateFrom, dateTo, debouncedSearch]);

  const { summary, results, isLoading, error, refetch } = useReportApi('/purchases/report', params);

  const buildExportData = () => results.map((p: any) => ({
    'Provider': p.provider?.name || '-',
    'Category': p.category?.name || '-',
    'Currency': p.currency?.code || '-',
    'Total': p.total,
    'Bill': p.bill ? `#${p.bill.id} ${p.bill.description || ''}`.trim() : '-',
    'Paid': fmtOrDash(purchasePaid(p)),
    'Remaining': fmtOrDash(purchaseRemaining(p)),
    'Status': p.purchase_status,
    'Order Date': p.orderDate || '-',
    'Delivery Date': p.deliveryDate || '-',
    'Items Count': p.itemsCount || 0,
    'Notes': p.notes || '-',
  }));

  const handleExport = () => exportToExcel(buildExportData(), `purchases_report_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Purchases Report');

  const handleClearFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setProviderFilter('all');
    setCategoryFilter('all'); setDateFrom(''); setDateTo('');
  };

  const mainStats: StatCard[] = [
    { label: 'إجمالي المشتريات', value: summary?.totalPurchases ?? 0, color: 'blue' },
    { label: 'إجمالي المبلغ', value: fmt(summary?.totalAmount), color: 'purple' },
    { label: 'متوسط الطلب', value: fmt(summary?.averagePurchase), color: 'green' },
    ...(summary?.totalPaid !== undefined ? [
      { label: 'إجمالي المدفوع', value: fmt(summary?.totalPaid), color: 'green' } as StatCard,
      { label: 'الصافي المتبقي', value: fmt(summary?.totalRemaining), color: 'red' } as StatCard,
    ] : []),
  ];

  const currencyStats: StatCard[] = Object.entries(summary?.byCurrency || {}).map(([code, c]: [string, any]) => ({
    label: `${code} — عدد ${c.count}`,
    value: fmt(c.total),
    color: 'blue' as const,
  }));

  const filters: FilterConfig[] = [
    { key: 'status', label: 'الحالة', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'جميع الحالات' }, { value: 'pending', label: 'قيد الانتظار' },
      { value: 'approved', label: 'معتمد' }, { value: 'delivered', label: 'مُسلم' },
    ]},
    { key: 'provider', label: 'المورد', type: 'select', value: providerFilter, onChange: setProviderFilter, options: [
      { value: 'all', label: 'جميع الموردين' },
      ...providersData.map((p: any) => ({ value: p.documentId || String(p.id), label: p.name })),
    ]},
    { key: 'category', label: 'الفئة', type: 'select', value: categoryFilter, onChange: setCategoryFilter, options: [
      { value: 'all', label: 'جميع الفئات' },
      ...(categoriesData as any[]).map((c: any) => ({ value: c.documentId || String(c.id), label: c.name })),
    ]},
    { key: 'dateFrom', label: 'من تاريخ', type: 'date', value: dateFrom, onChange: setDateFrom },
    { key: 'dateTo', label: 'إلى تاريخ', type: 'date', value: dateTo, onChange: setDateTo },
  ];

  if (error) return <ErrorState message={error} onRetry={refetch} entityName="purchases report" />;

  const headers = ['المورد', 'الفئة', 'العملة', 'الإجمالي', 'الفاتورة المرتبطة', 'المدفوع', 'الصافي المتبقي', 'الحالة', 'تاريخ الطلب', 'تاريخ التسليم', 'الملاحظات', 'الأصناف'];

  return (
    <div className="space-y-6" dir="rtl">
      <PageHeader title="تقرير المشتريات" description="عرض وتحليل جميع طلبات الشراء مع الأصناف لكل طلب" />
      <StatsCards stats={mainStats} columns={mainStats.length > 3 ? 5 : 3} />
      {currencyStats.length > 0 && (
        <div>
          <h3 className="text-md font-semibold text-gray-800 mb-2">الإجماليات حسب العملة</h3>
          <StatsCards stats={currencyStats} columns={4} />
        </div>
      )}
      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="البحث في الملاحظات..."
        filters={filters} onClearFilters={handleClearFilters}
        totalCount={results.length} filteredCount={results.length} entityName="تصفية التقرير"
        onExport={handleExport} onPrint={handlePrint}
      />

      {isLoading ? <TableSkeleton /> : (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">نتائج التقرير ({results.length} طلب شراء)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {headers.map(h => (
                    <th key={h} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((p: any) => {
                  const st = statusMap[p.purchase_status] || { label: p.purchase_status, cls: 'bg-gray-100 text-gray-800' };
                  const key = p.documentId;
                  const isExpanded = expandedPurchase === key;
                  return (
                    <React.Fragment key={key}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{p.provider?.name || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{p.category?.name || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{p.currency?.code || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{fmt(p.total)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                          {p.bill ? `#${p.bill.id} ${p.bill.description || ''}`.trim() : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700 text-right">{fmtOrDash(purchasePaid(p))}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-red-700 text-right">{fmtOrDash(purchaseRemaining(p))}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{p.orderDate ? new Date(p.orderDate).toLocaleDateString('ar-EG') : '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{p.deliveryDate ? new Date(p.deliveryDate).toLocaleDateString('ar-EG') : '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate text-right">{p.notes || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          <button type="button" onClick={() => setExpandedPurchase(isExpanded ? null : key)}
                            className="text-ocean-600 hover:text-ocean-800 font-medium">
                            {p.itemsCount || 0} {isExpanded ? '▲' : '▼'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={headers.length} className="px-4 py-2">
                            <PurchaseItemsDetails items={p.items || []} currencyCode={p.currency?.code || ''} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {results.length === 0 && (
            <div className="text-center py-8"><p className="text-gray-500">لا توجد مشتريات تطابق المعايير المحددة</p></div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default PurchasesReport;
